import { ACCOUNT_KEY } from './source-name.js';
import type { LeakageKind, LeakageReport } from './types.js';

/**
 * The Stats page's filters applied to the surfaces the server does not slice
 * for them. /api/summary and /api/data take account, rep, provider, feed and
 * country; the network table (/api/profit) and the leakage list (/api/leakage)
 * are whole-network reads, so choosing a provider at the top left the network
 * table showing every feed (2026-09-15). Both are exact per feed and per
 * account, so they are sliced here, on the client, under the same contract as
 * the server's source axis: a provider key is a slug (`swaarm`) or an account
 * (`acct:<id>`) for a mechanism source. A geo filter cannot slice either - a
 * feed row is the feed's whole traffic, a leak is a feed's or a bill's - so
 * under a geo filter alone they stay whole and the table's note says so. The
 * P&L hero stays the operating net whatever the filters, and says so
 * (pl-view.ts heroSub).
 */
export interface PageFilters {
  account?: string | undefined;
  rep?: string | undefined;
  provider?: string | undefined;
  feedId?: string | undefined;
  country?: string | undefined;
}

/** The server's `whereSource` contract (read/data.ts): an account key names one account, a slug names a provider. */
export function sourceMatches(key: string, accountId: string, provider: string | null): boolean {
  return key.startsWith(ACCOUNT_KEY) ? accountId === key.slice(ACCOUNT_KEY.length) : provider === key;
}

/** Whether any filter can slice a per-feed surface at all. */
export function slicesByFeed(fl: PageFilters): boolean {
  return !!(fl.account || fl.rep || fl.provider || fl.feedId);
}

export function filterFeedRows<T extends { accountId: string; feedId: string; rep: string | null; provider: string | null }>(
  rows: readonly T[],
  fl: PageFilters,
): T[] {
  if (!slicesByFeed(fl)) return [...rows];
  return rows.filter(
    (r) =>
      (!fl.account || r.accountId === fl.account) &&
      (!fl.rep || r.rep === fl.rep) &&
      (!fl.provider || sourceMatches(fl.provider, r.accountId, r.provider)) &&
      (!fl.feedId || r.feedId === fl.feedId),
  );
}

/** What the network table cannot do under a geo filter, in words; null when there is nothing to say. */
export function geoFilterNote(fl: PageFilters): string | null {
  return fl.country ? 'The geo filter does not slice this table: a row is the feed’s whole traffic in every country.' : null;
}

/**
 * The leakage report under the filters: items on a feed or an account that
 * the filters keep, the totals and counts re-summed from them. An item with no
 * account - a bill nobody owns, a destination with no feed - belongs to no
 * provider or rep, so it leaves whenever a per-feed filter is on.
 */
export function filterLeakage(
  r: LeakageReport | undefined,
  fl: PageFilters,
  accounts: ReadonlyMap<string, { rep: string; provider: string }>,
): LeakageReport | undefined {
  if (!r || !slicesByFeed(fl)) return r;
  const items = r.items.filter((it) => {
    if (!it.accountId) return false;
    const a = accounts.get(it.accountId);
    if (fl.account && it.accountId !== fl.account) return false;
    if (fl.feedId && it.feedId !== fl.feedId) return false;
    if (fl.rep && a?.rep !== fl.rep) return false;
    if (fl.provider && !sourceMatches(fl.provider, it.accountId, a?.provider ?? null)) return false;
    return true;
  });
  const counts = Object.fromEntries(Object.keys(r.counts).map((k) => [k, 0])) as Record<LeakageKind, number>;
  for (const it of items) counts[it.kind] = (counts[it.kind] ?? 0) + 1;
  const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
  return {
    ...r,
    items,
    counts,
    total: sum(items.filter((i) => !i.estimate).map((i) => i.amount)),
    estimated: sum(items.filter((i) => i.estimate).map((i) => i.amount)),
  };
}
